import React, { useState, useEffect } from 'react';
import { 
  Package, 
  Settings, 
  Terminal, 
  Monitor, 
  Upload, 
  Database, 
  Container,
  Menu,
  X,
  User,
  Bell,
  Search,
  Home,
  LogOut,
  Plus,
  Power,
  Server,
  Book,
  Shield,
  Cog,
  Ticket,
  DollarSign,
  Sparkles,
  Zap,
  Globe,
  BarChart3,
  Activity,
  MessageSquare,
  Calendar,
  Users,
  ChevronRight,
  Star,
  Layers
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthContext } from '../../hooks/useAuth';
// 按分类导入组件
import { Dashboard } from './dashboard';
import { 
  SubmitJob,
  AppCenter,
  JobManagement,
  SSHConnection,
  GraphicalInterface
} from './job-management';
import {
  FileTransfer,
  DataSets,
  ImageCenter
} from './data-management';
import {
  ClusterConfig,
  UserManual,
  QuotaManagement,
  SystemManagement
} from './system-management';
import {
  BillingCenter,
  KeyPairManagement,
  VoucherManagement,
  TicketManagement,
  PricingStandard,
  Promotion
} from './user-center';


interface MenuItem {
  id: string;
  label: string;
  icon: React.ElementType;
  component: React.ComponentType;
}

interface MenuSection {
  title?: string;
  items: MenuItem[];
}

const Console: React.FC = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [hoverTimeout, setHoverTimeout] = useState<NodeJS.Timeout | null>(null);
  const { user, isAuthenticated, isLoading, logout } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();

  // 调试用：监听 userDropdownOpen 状态变化
  useEffect(() => {
    console.log('🔄 用户下拉菜单状态变化:', userDropdownOpen ? '开启' : '关闭');
  }, [userDropdownOpen]);

  // 从URL hash同步activeTab
  useEffect(() => {
    const hash = location.hash.slice(2); // Remove '#/' from the hash
    if (hash) {
      setActiveTab(hash);
    } else {
      // If no hash is present, set default tab and update URL
      setActiveTab('dashboard');
      navigate('/console#/dashboard', { replace: true });
    }
  }, [location.hash, navigate]);

  // 认证保护：如果未登录，重定向到首页
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, isLoading, navigate]);

  // 清理悬停超时
  useEffect(() => {
    return () => {
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
      }
    };
  }, [hoverTimeout]);

  // 更新activeTab时同时更新URL hash
  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    navigate(`/console#/${tabId}`, { replace: true });
  };

  const menuSections: MenuSection[] = [
    {
      items: [
        { id: 'submit', label: '提交作业', icon: Plus, component: SubmitJob },
      ]
    },
    {
      items: [
        { id: 'dashboard', label: '仪表盘', icon: Power, component: Dashboard },
      ]
    },
    {
      title: '作业',
      items: [
        { id: 'appCenter', label: '应用中心', icon: Package, component: AppCenter },
        { id: 'jobManagement', label: '作业管理', icon: Settings, component: JobManagement },
        { id: 'sshConnection', label: 'SSH连接', icon: Terminal, component: SSHConnection },
        { id: 'graphicalInterface', label: '图形界面', icon: Monitor, component: GraphicalInterface },
      ]
    },
    {
      title: '数据',
      items: [
        { id: 'fileTransfer', label: '文件传输', icon: Upload, component: FileTransfer },
        { id: 'dataSets', label: '数据集', icon: Database, component: DataSets },
        { id: 'imageCenter', label: '镜像中心', icon: Container, component: ImageCenter },
      ]
    },
    {
      title: '管理',
      items: [
        { id: 'clusterConfig', label: '集群配置', icon: Server, component: ClusterConfig },
        { id: 'userManual', label: '用户手册', icon: Book, component: UserManual },
      ]
    }
  ];

  const allMenuItems = menuSections.flatMap(section => section.items);
  
  // 处理特殊页面（如费用中心、安全管理、系统管理和代金券）
  const getActiveComponent = () => {
    if (activeTab === 'billing') {
      return BillingCenter;
    }
    if (activeTab === 'security') {
      return KeyPairManagement;
    }
    if (activeTab === 'keyPairManagement') {
      return KeyPairManagement;
    }
    if (activeTab === 'system') {
      return SystemManagement;
    }
    if (activeTab === 'voucher') {
      return VoucherManagement;
    }
    if (activeTab === 'promotion') {
      return Promotion;
    }
    if (activeTab === 'ticketManagement') {
      return TicketManagement;
    }
    if (activeTab === 'pricingStandard') {
      return PricingStandard;
    }
    return allMenuItems.find(item => item.id === activeTab)?.component || Dashboard;
  };
  
  const ActiveComponent = getActiveComponent();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // 加载状态
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <div className="text-center">
          <div className="relative">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-transparent bg-gradient-to-r from-blue-500 to-purple-600 mx-auto"></div>
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200 border-t-transparent mx-auto absolute top-0"></div>
          </div>
          <div className="mt-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">ChainCloud 正在启动</h3>
            <p className="text-gray-600">请稍候，正在为您准备最佳体验...</p>
          </div>
        </div>
      </div>
    );
  }

  // 未认证状态
  if (!isAuthenticated) {
    return null; // 会被重定向到首页
  }

  return (
    <div className="flex h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      {/* Sidebar */}
      <div className={`
        ${sidebarOpen ? 'console-sidebar' : 'console-sidebar-collapsed'} 
        portrait:${sidebarOpen ? '!w-56 md:!w-64' : '!w-12 md:!w-16'} 
        landscape:${sidebarOpen ? '!w-64 lg:!w-72 xl:!w-80 2xl:!w-80 3xl:!w-96' : '!w-16 lg:!w-20 xl:!w-24'} 
        bg-white/80 backdrop-blur-xl border-r border-white/20 shadow-2xl 
        transition-all duration-500 ease-in-out flex flex-col relative h-full overflow-hidden
      `}>
        {/* Decorative background */}
        <div className="absolute inset-0 bg-gradient-to-b from-blue-500/5 via-purple-500/5 to-indigo-500/5"></div>
        <div className="absolute top-0 right-0 w-24 h-24 lg:w-32 lg:h-32 xl:w-40 xl:h-40 bg-gradient-to-br from-blue-400/10 to-purple-600/10 rounded-full blur-xl transform translate-x-8 -translate-y-8"></div>
        <div className="absolute bottom-0 left-0 w-16 h-16 lg:w-24 lg:h-24 xl:w-32 xl:h-32 bg-gradient-to-tr from-indigo-400/10 to-cyan-600/10 rounded-full blur-xl transform -translate-x-4 translate-y-4"></div>
        
        {/* Logo */}
        <div className="relative p-4 lg:p-6 xl:p-8 portrait:p-3 portrait:md:p-4 border-b border-white/10">
          <div className="flex items-center space-x-3 lg:space-x-4 portrait:space-x-2">
            <div className="relative">
              <div className="w-10 h-10 lg:w-12 lg:h-12 xl:w-14 xl:h-14 portrait:w-8 portrait:h-8 portrait:md:w-10 portrait:md:h-10 bg-gradient-to-br from-blue-500 via-purple-600 to-indigo-700 rounded-xl lg:rounded-2xl portrait:rounded-lg flex items-center justify-center shadow-lg">
                <Sparkles className="w-5 h-5 lg:w-7 lg:h-7 xl:w-8 xl:h-8 portrait:w-4 portrait:h-4 portrait:md:w-5 portrait:md:h-5 text-white" />
              </div>
              <div className="absolute -top-1 -right-1 w-3 h-3 lg:w-4 lg:h-4 portrait:w-2 portrait:h-2 portrait:md:w-3 portrait:md:h-3 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full animate-pulse"></div>
            </div>
            {sidebarOpen && (
              <div className="transition-all duration-300">
                <h1 className="text-xl lg:text-2xl xl:text-3xl portrait:text-lg portrait:md:text-xl font-bold bg-gradient-to-r from-gray-800 via-blue-800 to-purple-800 bg-clip-text text-transparent">
                  ChainCloud
                </h1>
                <p className="text-xs lg:text-sm xl:text-base portrait:text-[10px] portrait:md:text-xs text-gray-500 font-medium">AI云超算平台</p>
              </div>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 min-h-0 p-3 lg:p-4 xl:p-6 portrait:p-2 portrait:md:p-3 portrait:lg:p-4 overflow-y-auto scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-200 hover:scrollbar-thumb-gray-300 relative">
          {menuSections.map((section, sectionIndex) => (
            <div key={sectionIndex} className={sectionIndex > 0 ? 'mt-6 lg:mt-8 portrait:mt-4 portrait:md:mt-5' : ''}>
              {/* 分类标题 */}
              {section.title && sidebarOpen && (
                <div className="px-3 lg:px-4 portrait:px-2 portrait:md:px-3 mb-3 lg:mb-4 portrait:mb-2 portrait:md:mb-3">
                  <h3 className="text-xs lg:text-sm xl:text-base portrait:text-[10px] portrait:md:text-xs font-bold text-gray-400 uppercase tracking-widest flex items-center whitespace-nowrap">
                    <div className="w-1.5 h-1.5 lg:w-2 lg:h-2 portrait:w-1 portrait:h-1 portrait:md:w-1.5 portrait:md:h-1.5 bg-gradient-to-r from-blue-400 to-purple-500 rounded-full mr-2 portrait:mr-1 portrait:md:mr-1.5 flex-shrink-0"></div>
                    <span className="truncate max-w-full">{section.title}</span>
                  </h3>
                </div>
              )}
              
              {/* 菜单项 */}
              <ul className="space-y-1.5 lg:space-y-2 portrait:space-y-1 portrait:md:space-y-1.5">
                {section.items.map((item) => {
                  const Icon = item.icon;
                  const isActive = activeTab === item.id;
                  const isSubmitJob = item.id === 'submit';
                  
                  return (
                    <li key={item.id} className="relative">
                      <button
                        onClick={() => handleTabChange(item.id)}
                        className={`w-full flex items-center space-x-2 sm:space-x-3 lg:space-x-4 portrait:space-x-2 portrait:md:space-x-3 px-2 sm:px-3 lg:px-4 xl:px-5 portrait:px-2 portrait:md:px-3 py-2 sm:py-2.5 lg:py-3.5 xl:py-4 portrait:py-2 portrait:md:py-2.5 rounded-lg lg:rounded-xl portrait:rounded-md portrait:md:rounded-lg transition-all duration-300 group relative overflow-hidden min-w-0 ${
                          isSubmitJob
                            ? 'bg-gradient-to-r from-blue-600 via-blue-700 to-purple-700 text-white shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/40 hover:scale-[1.02] portrait:hover:scale-100 transform'
                            : isActive
                            ? 'bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 shadow-md border border-blue-200/50 backdrop-blur-sm'
                            : 'text-gray-600 hover:bg-white/50 hover:text-gray-800 hover:shadow-md backdrop-blur-sm'
                        }`}
                      >
                        {/* 背景装饰 */}
                        {(isSubmitJob || isActive) && (
                          <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent opacity-50"></div>
                        )}
                        
                        {/* 图标 */}
                        <div className={`relative flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 xl:w-10 xl:h-10 portrait:w-5 portrait:h-5 portrait:md:w-6 portrait:md:h-6 rounded-md lg:rounded-lg portrait:rounded-sm portrait:md:rounded-md transition-all duration-300 flex-shrink-0 ${
                          isSubmitJob 
                            ? 'bg-white/20 shadow-lg' 
                            : isActive 
                            ? 'bg-blue-100 shadow-sm' 
                            : 'group-hover:bg-white/80 group-hover:shadow-sm'
                        }`}>
                          <Icon className={`w-3 h-3 sm:w-4 sm:h-4 lg:w-5 lg:h-5 xl:w-6 xl:h-6 portrait:w-3 portrait:h-3 portrait:md:w-4 portrait:md:h-4 transition-all duration-300 ${
                            isSubmitJob ? 'text-white' : isActive ? 'text-blue-600' : 'text-current'
                          }`} />
                        </div>
                        
                        {sidebarOpen && (
                          <div className="flex-1 text-left min-w-0 overflow-hidden max-w-full">
                            <span className={`font-semibold text-xs sm:text-sm lg:text-base xl:text-lg portrait:text-xs portrait:md:text-sm transition-all duration-300 whitespace-nowrap block truncate max-w-full ${
                              isSubmitJob ? 'text-white' : isActive ? 'text-blue-700' : 'text-current'
                            }`}>
                              {item.label}
                            </span>
                            {isActive && (
                              <div className="w-6 sm:w-8 lg:w-12 xl:w-16 portrait:w-6 portrait:md:w-8 h-0.5 bg-gradient-to-r from-blue-400 to-purple-500 mt-1 portrait:mt-0.5 rounded-full flex-shrink-0"></div>
                            )}
                          </div>
                        )}
                        
                        {/* 活跃状态指示器 */}
                        {isActive && (
                          <div className="absolute right-2 lg:right-3 portrait:right-1 portrait:md:right-2 w-1.5 h-1.5 lg:w-2 lg:h-2 portrait:w-1 portrait:h-1 portrait:md:w-1.5 portrait:md:h-1.5 bg-gradient-to-r from-blue-400 to-purple-500 rounded-full animate-pulse"></div>
                        )}
                        
                        {/* 悬停效果 */}
                        {!isSubmitJob && !isActive && (
                          <ChevronRight className="w-3 h-3 lg:w-4 lg:h-4 xl:w-5 xl:h-5 portrait:w-2.5 portrait:h-2.5 portrait:md:w-3 portrait:md:h-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-x-2 group-hover:translate-x-0" />
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>

        {/* User Profile */}
        {sidebarOpen && (
          <div className="flex-shrink-0 relative p-4 lg:p-6 xl:p-8 portrait:p-3 portrait:md:p-4">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl lg:rounded-2xl portrait:rounded-lg portrait:md:rounded-xl p-3 lg:p-4 xl:p-5 portrait:p-2 portrait:md:p-3 border border-blue-100/50 backdrop-blur-sm">
              <div className="flex items-center space-x-3 lg:space-x-4 portrait:space-x-2 portrait:md:space-x-3">
                <div className="relative flex-shrink-0">
                  <div className="w-10 h-10 lg:w-12 lg:h-12 xl:w-14 xl:h-14 portrait:w-8 portrait:h-8 portrait:md:w-10 portrait:md:h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl lg:rounded-2xl portrait:rounded-lg portrait:md:rounded-xl flex items-center justify-center shadow-lg">
                    {user?.avatar ? (
                      <img src={user.avatar} alt={user.username} className="w-10 h-10 lg:w-12 lg:h-12 xl:w-14 xl:h-14 portrait:w-8 portrait:h-8 portrait:md:w-10 portrait:md:h-10 rounded-xl lg:rounded-2xl portrait:rounded-lg portrait:md:rounded-xl object-cover" />
                    ) : (
                      <User className="w-5 h-5 lg:w-6 lg:h-6 xl:w-7 xl:h-7 portrait:w-4 portrait:h-4 portrait:md:w-5 portrait:md:h-5 text-white" />
                    )}
                  </div>
                  <div className="absolute -bottom-1 -right-1 w-3 h-3 lg:w-4 lg:h-4 portrait:w-2.5 portrait:h-2.5 portrait:md:w-3 portrait:md:h-3 bg-green-500 rounded-full border-2 border-white"></div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm lg:text-base xl:text-lg portrait:text-xs portrait:md:text-sm font-bold text-gray-800 truncate whitespace-nowrap max-w-full">{user?.username}</p>
                  <p className="text-xs lg:text-sm portrait:text-[10px] portrait:md:text-xs text-gray-500 truncate whitespace-nowrap max-w-full">{user?.email}</p>
                  <div className="flex items-center mt-1 portrait:mt-0.5 max-w-full">
                    <Star className="w-3 h-3 lg:w-4 lg:h-4 portrait:w-2.5 portrait:h-2.5 portrait:md:w-3 portrait:md:h-3 text-yellow-500 mr-1 portrait:mr-0.5 flex-shrink-0" />
                    <span className="text-xs lg:text-sm portrait:text-[10px] portrait:md:text-xs font-medium text-yellow-600 bg-yellow-50 px-2 py-0.5 portrait:px-1.5 portrait:py-0.25 rounded-full whitespace-nowrap truncate">标准版</span>
                  </div>
                </div>
                <button
                  onClick={handleLogout}
                  className="flex-shrink-0 p-2 lg:p-3 portrait:p-1.5 portrait:md:p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg lg:rounded-xl portrait:rounded-md portrait:md:rounded-lg transition-all duration-300 hover:scale-110 portrait:hover:scale-105"
                  title="退出登录"
                >
                  <LogOut className="w-4 h-4 lg:w-5 lg:h-5 portrait:w-3 portrait:h-3 portrait:md:w-4 portrait:md:h-4" />
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* 收缩时的用户头像 */}
        {!sidebarOpen && (
          <div className="flex-shrink-0 p-3 lg:p-4 xl:p-5 portrait:p-2 portrait:md:p-3">
            <div className="relative">
              <div className="w-10 h-10 lg:w-12 lg:h-12 xl:w-14 xl:h-14 portrait:w-8 portrait:h-8 portrait:md:w-10 portrait:md:h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl lg:rounded-2xl portrait:rounded-lg portrait:md:rounded-xl flex items-center justify-center shadow-lg mx-auto">
                {user?.avatar ? (
                  <img src={user.avatar} alt={user.username} className="w-10 h-10 lg:w-12 lg:h-12 xl:w-14 xl:h-14 portrait:w-8 portrait:h-8 portrait:md:w-10 portrait:md:h-10 rounded-xl lg:rounded-2xl portrait:rounded-lg portrait:md:rounded-xl object-cover" />
                ) : (
                  <User className="w-5 h-5 lg:w-6 lg:h-6 xl:w-7 xl:h-7 portrait:w-4 portrait:h-4 portrait:md:w-5 portrait:md:h-5 text-white" />
                )}
              </div>
              <div className="absolute -bottom-1 -right-1 w-3 h-3 lg:w-4 lg:h-4 portrait:w-2.5 portrait:h-2.5 portrait:md:w-3 portrait:md:h-3 bg-green-500 rounded-full border-2 border-white"></div>
            </div>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-visible">
        {/* Top Navigation */}
        <header className="console-header bg-white/80 backdrop-blur-xl border-b border-white/20 shadow-lg relative overflow-visible z-[100]">
          {/* 装饰性背景 */}
          <div className="absolute inset-0 bg-gradient-to-r from-blue-50/50 via-white to-purple-50/50"></div>
          <div className="absolute top-0 left-1/4 w-64 lg:w-96 xl:w-[32rem] h-1 bg-gradient-to-r from-blue-400 via-purple-500 to-indigo-600"></div>
          
          <div className="relative px-4 lg:px-6 xl:px-8 py-3 lg:py-4 xl:py-5">
            <div className="flex items-center gap-4 min-w-0 w-full">
              {/* 最左侧 - 菜单缩放按钮 */}
              <div className="flex-shrink-0">
                <button
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                  className="p-2 lg:p-3 rounded-xl lg:rounded-2xl hover:bg-white/80 transition-all duration-300 hover:shadow-lg hover:scale-105 group"
                >
                  {sidebarOpen ? 
                    <ChevronRight className="w-4 h-4 lg:w-5 lg:h-5 text-gray-600 group-hover:text-blue-600 transition-colors rotate-180" /> : 
                    <Menu className="w-4 h-4 lg:w-5 lg:h-5 text-gray-600 group-hover:text-blue-600 transition-colors" />
                  }
                </button>
              </div>

              {/* 中间区域 - 搜索框 */}
              <div className="flex-1 flex justify-center min-w-0 max-w-2xl mx-auto search-container">
                <div className="relative w-full max-w-md lg:max-w-lg xl:max-w-xl">
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-xl lg:rounded-2xl blur"></div>
                  <div className="relative bg-white/80 backdrop-blur-sm border border-white/40 rounded-xl lg:rounded-2xl shadow-lg">
                    <Search className="w-4 h-4 lg:w-5 lg:h-5 absolute left-3 lg:left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="搜索功能、文档、设置..."
                      className="w-full pl-10 lg:pl-12 pr-4 lg:pr-6 py-2.5 lg:py-3 xl:py-4 bg-transparent border-0 rounded-xl lg:rounded-2xl focus:ring-2 focus:ring-blue-500/50 focus:outline-none text-gray-700 placeholder-gray-400 text-sm lg:text-base"
                    />
                  </div>
                </div>
              </div>

              {/* 最右侧区域 - 快速按钮和用户菜单 */}
              <div className="flex items-center space-x-2 lg:space-x-3 flex-shrink-0 nav-right">
                {/* 快速访问按钮 - 只在超大屏显示 */}
                <div className="hidden 2xl:flex items-center space-x-2">
                  <button className="px-3 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:shadow-lg hover:scale-105 transition-all duration-300 text-sm font-medium flex items-center space-x-1.5">
                    <Zap className="w-4 h-4" />
                    <span>快速部署</span>
                  </button>
                  <button className="px-3 py-2 bg-white/80 text-gray-700 rounded-lg hover:shadow-lg hover:scale-105 transition-all duration-300 text-sm font-medium border border-gray-200/50 flex items-center space-x-1.5">
                    <Activity className="w-4 h-4" />
                    <span>监控</span>
                  </button>
                </div>
                {/* 通知按钮 */}
                <button className="relative p-2 lg:p-3 rounded-xl lg:rounded-2xl hover:bg-white/80 transition-all duration-300 hover:shadow-lg group flex-shrink-0">
                  <Bell className="icon-responsive-sm text-gray-600 group-hover:text-blue-600 transition-colors" />
                  <div className="absolute -top-1 -right-1 w-4 h-4 lg:w-5 lg:h-5 bg-gradient-to-r from-red-400 to-pink-500 rounded-full flex items-center justify-center">
                    <span className="text-xs font-bold text-white">3</span>
                  </div>
                </button>
                
                {/* 用户下拉菜单 */}
                <div 
                  className="relative group user-dropdown-container flex-shrink-0"
                  style={{ marginLeft: 'auto', flexShrink: 0, flexGrow: 0 }}
                  onMouseEnter={() => {
                    if (hoverTimeout) {
                      clearTimeout(hoverTimeout);
                      setHoverTimeout(null);
                    }
                    setUserDropdownOpen(true);
                  }}
                  onMouseLeave={() => {
                    const timeout = setTimeout(() => {
                      setUserDropdownOpen(false);
                    }, 300);
                    setHoverTimeout(timeout);
                  }}
                >
                  <button 
                    className="flex items-center space-x-2 lg:space-x-3 p-2 rounded-xl lg:rounded-2xl hover:bg-white/80 transition-all duration-300 hover:shadow-lg cursor-pointer"
                  >
                    <div className="relative">
                      <div className="w-8 h-8 lg:w-10 lg:h-10 xl:w-12 xl:h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl lg:rounded-2xl flex items-center justify-center shadow-lg">
                        {user?.avatar ? (
                          <img src={user.avatar} alt={user.username} className="w-8 h-8 lg:w-10 lg:h-10 xl:w-12 xl:h-12 rounded-xl lg:rounded-2xl object-cover" />
                        ) : (
                          <User className="w-4 h-4 lg:w-5 lg:h-5 xl:w-6 xl:h-6 text-white" />
                        )}
                      </div>
                      <div className="absolute -bottom-1 -right-1 w-3 h-3 lg:w-4 lg:h-4 bg-green-500 rounded-full border-2 border-white"></div>
                    </div>
                    <div className="text-left hidden lg:block">
                      <div className="text-sm lg:text-base xl:text-lg font-bold text-gray-800">{user?.username || '访客_mcd030nm'}</div>
                      <div className="flex items-center space-x-2">
                        <Star className="w-3 h-3 lg:w-4 lg:h-4 text-yellow-500" />
                        <span className="text-xs lg:text-sm font-medium text-yellow-600">标准版</span>
                      </div>
                    </div>
                  </button>
                  
                  {/* 用户下拉菜单 */}
                  {userDropdownOpen && (
                    <div 
                      className="absolute top-full right-0 mt-2 w-56 bg-white rounded-xl shadow-2xl border border-gray-200 py-2 z-[99999] opacity-100 scale-100 transition-all duration-200"
                      onMouseEnter={() => {
                        if (hoverTimeout) {
                          clearTimeout(hoverTimeout);
                          setHoverTimeout(null);
                        }
                      }}
                      onMouseLeave={() => {
                        setUserDropdownOpen(false);
                      }}
                    >
                      <div className="px-4 py-3 border-b border-gray-100">
                        <div className="text-sm font-medium text-gray-900">{user?.username || '访客_mcd030nm'}</div>
                        <div className="text-xs text-gray-500 hover:text-blue-600 cursor-pointer transition-colors">成为合作伙伴 &gt;</div>
                      </div>
                      
                      <button 
                        onClick={() => {
                          handleTabChange('billing');
                          setUserDropdownOpen(false);
                        }}
                        className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <div className="w-6 h-6 bg-gradient-to-r from-yellow-100 to-orange-100 rounded-lg flex items-center justify-center">
                          <DollarSign className="w-3 h-3 text-yellow-600" />
                        </div>
                        <span>费用中心</span>
                      </button>
                      
                      <button 
                        onClick={() => {
                          handleTabChange('pricingStandard');
                          setUserDropdownOpen(false);
                        }}
                        className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <div className="w-6 h-6 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-lg flex items-center justify-center">
                          <BarChart3 className="w-3 h-3 text-blue-600" />
                        </div>
                        <span>收费标准</span>
                      </button>
                      
                      <button 
                        onClick={() => {
                          handleTabChange('voucher');
                          setUserDropdownOpen(false);
                        }}
                        className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <div className="w-6 h-6 bg-gradient-to-r from-purple-100 to-pink-100 rounded-lg flex items-center justify-center">
                          <Ticket className="w-3 h-3 text-purple-600" />
                        </div>
                        <span>代金券</span>
                      </button>
                      
                      <button 
                        onClick={() => {
                          handleTabChange('promotion');
                          setUserDropdownOpen(false);
                        }}
                        className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <div className="w-6 h-6 bg-gradient-to-r from-green-100 to-emerald-100 rounded-lg flex items-center justify-center">
                          <Sparkles className="w-3 h-3 text-green-600" />
                        </div>
                        <span>我的推广</span>
                      </button>
                      
                      <button 
                        onClick={() => {
                          handleTabChange('ticketManagement');
                          setUserDropdownOpen(false);
                        }}
                        className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <div className="w-6 h-6 bg-gradient-to-r from-gray-100 to-slate-100 rounded-lg flex items-center justify-center">
                          <MessageSquare className="w-3 h-3 text-gray-600" />
                        </div>
                        <span>工单管理</span>
                      </button>
                      
                      <button 
                        onClick={() => {
                          handleTabChange('keyPairManagement');
                          setUserDropdownOpen(false);
                        }}
                        className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <div className="w-6 h-6 bg-gradient-to-r from-indigo-100 to-blue-100 rounded-lg flex items-center justify-center">
                          <Shield className="w-3 h-3 text-indigo-600" />
                        </div>
                        <span>安全管理</span>
                      </button>
                      
                      <button 
                        onClick={() => {
                          handleTabChange('system');
                          setUserDropdownOpen(false);
                        }}
                        className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <div className="w-6 h-6 bg-gradient-to-r from-teal-100 to-cyan-100 rounded-lg flex items-center justify-center">
                          <Cog className="w-3 h-3 text-teal-600" />
                        </div>
                        <span>系统管理</span>
                      </button>
                      
                      <div className="border-t border-gray-100 mt-2 pt-2">
                        <button 
                          onClick={() => {
                            handleLogout();
                            setUserDropdownOpen(false);
                          }}
                          className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                        >
                          <div className="w-6 h-6 bg-gradient-to-r from-red-100 to-pink-100 rounded-lg flex items-center justify-center">
                            <LogOut className="w-3 h-3 text-red-600" />
                          </div>
                          <span>退出登录</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content Area */}
        <main className="console-content bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 relative">
          {/* 装饰性背景点 - 使用负z-index确保不会遮挡任何UI元素 */}
          <div className="absolute inset-0 opacity-30 z-[-1] pointer-events-none">
            <div className="absolute top-16 lg:top-20 xl:top-24 left-16 lg:left-20 xl:left-24 w-2 h-2 lg:w-3 lg:h-3 bg-blue-400 rounded-full animate-pulse"></div>
            <div className="absolute top-32 lg:top-40 xl:top-48 right-24 lg:right-32 xl:right-40 w-1 h-1 lg:w-1.5 lg:h-1.5 bg-purple-400 rounded-full animate-pulse delay-1000"></div>
            <div className="absolute bottom-24 lg:bottom-32 xl:bottom-40 left-32 lg:left-40 xl:left-48 w-1.5 h-1.5 lg:w-2 lg:h-2 bg-indigo-400 rounded-full animate-pulse delay-2000"></div>
          </div>
          
          <div className="relative z-[1]">
            <ActiveComponent />
          </div>
        </main>
      </div>
    </div>
  );
};

export default Console; 